import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { formatCurrency } from '../lib/utils';
import axios from 'axios';
import { Plus, Search, Edit2, Trash2, Users, CreditCard, Banknote, Landmark } from 'lucide-react';
import ExportButton from '../components/ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomersPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'accountant';
  const [customers, setCustomers] = useState([]);
  const [debtStats, setDebtStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
  });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const [custRes, statsRes] = await Promise.allSettled([
        axios.get(`${API}/customers`),
        axios.get(`${API}/customers/debt-stats`),
      ]);
      if (custRes.status === 'fulfilled') setCustomers(custRes.value.data);
      if (statsRes.status === 'fulfilled') setDebtStats(statsRes.value.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData);
      } else {
        await axios.post(`${API}/customers`, formData);
      }
      fetchCustomers();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving customer:', error);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/debt-payments`, {
        customer_id: selectedCustomer.id,
        customer_name: selectedCustomer.name,
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
        notes: paymentNotes,
      });
      fetchCustomers();
      setPaymentDialogOpen(false);
      setSelectedCustomer(null);
      setPaymentAmount('');
      setPaymentNotes('');
      setPaymentMethod('cash');
    } catch (error) {
      console.error('Error recording payment:', error);
      const msg = error.response?.data?.detail || (language === 'ar' ? 'حدث خطأ' : 'Error');
      alert(msg);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm'))) {
      try {
        await axios.delete(`${API}/customers/${id}`);
        fetchCustomers();
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      notes: customer.notes || '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCustomer(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
      notes: '',
    });
  };

  const openPaymentDialog = (customer) => {
    setSelectedCustomer(customer);
    setPaymentDialogOpen(true);
  };

  const filteredCustomers = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.phone && c.phone.includes(search))
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl" data-testid="customers-title">{t('customers')}</h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'إدارة العملاء والديون' : 'Manage customers and debts'}
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButton
            endpoint="customers"
            filename="customers"
            label={language === 'ar' ? 'تصدير Excel' : 'Export Excel'}
          />
          <Button onClick={() => setDialogOpen(true)} data-testid="add-customer-btn">
            <Plus size={18} className="me-2" />
            {t('addCustomer')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="border-border/40">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder={t('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-10"
              data-testid="customers-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card className="border-border/40">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users size={48} className="mx-auto mb-2 opacity-50" />
              <p>{t('noData')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('customerName')}</TableHead>
                    <TableHead>{t('phone')}</TableHead>
                    <TableHead>{language === 'ar' ? 'ديون الأسبوع' : 'Week Debts'}</TableHead>
                    <TableHead>{language === 'ar' ? 'إجمالي الديون' : 'Total Debts'}</TableHead>
                    {isAdmin && <TableHead>{t('assignedDriver')}</TableHead>}
                    <TableHead className="text-end">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.map((customer) => (
                    <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                      <TableCell className="font-medium">{customer.name}</TableCell>
                      <TableCell dir="ltr">{customer.phone || '-'}</TableCell>
                      <TableCell>
                        <span className={`font-bold ${(debtStats[customer.id]?.week_debt || 0) > 0 ? 'text-orange-500' : 'text-muted-foreground'}`}>
                          {formatCurrency(debtStats[customer.id]?.week_debt || 0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`font-bold ${(debtStats[customer.id]?.total_debt || 0) > 0 ? 'text-destructive' : 'text-green-600'}`}>
                          {formatCurrency(debtStats[customer.id]?.total_debt || 0)}
                        </span>
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          {customer.assigned_driver_name ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                              {customer.assigned_driver_name}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          {(customer.total_debt > 0 || (debtStats[customer.id]?.total_debt || 0) > 0) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openPaymentDialog(customer)}
                              data-testid={`pay-debt-${customer.id}`}
                            >
                              <CreditCard size={14} className="me-1" />
                              {t('recordPayment')}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(customer)}
                            data-testid={`edit-customer-${customer.id}`}
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(customer.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-customer-${customer.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingCustomer ? t('edit') : t('addCustomer')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('customerName')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="customer-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('phone')}</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  dir="ltr"
                  data-testid="customer-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('email')}</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="customer-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('address')}</Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={2}
                data-testid="customer-address-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="customer-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{t('recordPayment')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePayment} className="space-y-4">
            {selectedCustomer && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">{t('customerName')}</p>
                <p className="font-medium">{selectedCustomer.name}</p>
                <p className="text-sm text-muted-foreground mt-2">{language === 'ar' ? 'الدين الحالي' : 'Current Debt'}</p>
                <p className="text-xl font-bold text-destructive">
                  {formatCurrency(selectedCustomer.total_debt)}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('amount')}</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                max={selectedCustomer?.total_debt}
                required
                data-testid="payment-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'ar' ? 'طريقة الدفع' : 'Payment Method'}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('cash')}
                  className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-medium transition-colors ${
                    paymentMethod === 'cash'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                  data-testid="payment-method-cash"
                >
                  <Banknote size={16} />
                  {language === 'ar' ? 'نقداً' : 'Cash'}
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod('bank')}
                  className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-medium transition-colors ${
                    paymentMethod === 'bank'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                  data-testid="payment-method-bank"
                >
                  <Landmark size={16} />
                  {language === 'ar' ? 'تحويل بنكي' : 'Bank'}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{language === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                rows={2}
                data-testid="payment-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="payment-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
